import { Component, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit, inject } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { auditTime, combineLatest, debounceTime, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import { FilterType } from '../../enums/filter-type.enum';
import { FilterConfig } from '../../model/filter-config.interface';
import { SubSink } from 'SubSink';
import { FilterPresetDto } from 'src/app/models/dtos/filter-preset-dto';
import { ScreenService } from 'src/app/core/screen.service';
import { FilterService } from '../../services/filter.service';
import { MatDialog } from '@angular/material/dialog';
import { FilterPresetNameFormDialogComponent } from 'src/app/shared/components/filters/filter-preset-name-form-dialog/filter-preset-name-form-dialog.component';
import { FilterDictionaryItem } from "../../../models/FilterDictionaryItem";

@Component({
  selector: 'app-list-filter',
  templateUrl: './list-filter.component.html',
  styleUrl: './list-filter.component.scss',
  standalone: false
})
export class ListFilterComponent implements OnInit, OnDestroy, AfterViewInit {
  private formBuilder = inject(FormBuilder);
  screenService = inject(ScreenService);
  private filterService = inject(FilterService);
  private dialog = inject(MatDialog);
  private changeDetectionRef = inject(ChangeDetectorRef);

  public formGroup: FormGroup;
  public filterselectorFormGroup: FormGroup;
  public filters: FilterConfig[] = [];
  public enabledFilters: FilterConfig[]; public filterPresets: FilterPresetDto[];
  public readonly defaultFilterPresetName = 'default';
  public readonly showFilterPresetSelector$ = this.filterService.filterPresets$.pipe(map(presets => presets?.filter(p => p.Name !== this.defaultFilterPresetName).length));
  public readonly isSavedFilterPreset$ = this.filterService.selectedFilterPreset$.pipe(map(preset => preset && preset.Name !== this.defaultFilterPresetName));
  public readonly presets$ = this.filterService.filterPresets$;
  public readonly selectedPresetId$ = this.filterService.selectedFilterPresetId$;
  private readonly subs = new SubSink();

  public get FilterType() {
    return FilterType;
  }

  public getPagedFilters = (value: string, page: number, pageSize: number) => of(this.filters).pipe(
    map(filters => filters.map(filter => ({ Id: filter.id, Name: filter.label ?? filter.name, Description: filter.description } as FilterDictionaryItem))),
    map(filters => filters.filter(filter => !value || filter.Name.toLowerCase().includes(value.toLowerCase()))),
    map(filters => ({ Count: filters.length, Results: filters.splice((page - 1) * pageSize, pageSize) }))
  );

  changeFilters = (values: FilterDictionaryItem[]) => {
    if (!values) {
      return;
    }
    this.filters.forEach(filter => {
      const isEnabled = values.some(v => v.Id === filter.id);
      if (this.enabledFilters.includes(filter) !== isEnabled) {
        if (isEnabled) {
          this.addFilter(filter);
        } else {
          this.removeFilter(filter);
        }
      }
    });
  }


  constructor() {
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.formGroup = this.formBuilder.group({});
    this.filterselectorFormGroup = this.formBuilder.group({});
  }
  ngAfterViewInit() {
    this.subs.sink = combineLatest([this.filterService.config$, this.filterService.filtersPreset$])
      .pipe(tap(([config, _]) => {
        this.filters = [...config];
        this.changeDetectionRef.detectChanges();
      }))
      .pipe(debounceTime(300))
      .pipe(tap(([_, preset]) => {
        if (preset) {
          this.enabledFilters = Object.keys(preset).reduce((enabledFilters, property) => {
            const matchingFilter = this.filters.find(
              (filter) =>
                !enabledFilters.includes(filter) && (Array.isArray(filter.name) ? filter.name.includes(property) : filter.name === property),
            );

            if (matchingFilter) {
              enabledFilters.push(matchingFilter);
            }

            return enabledFilters;
          }, []);
        } else if (!this.enabledFilters) {
          this.enabledFilters = this.filters.filter((filter) => filter.default);
        }

        this.filterselectorFormGroup.patchValue({
          filtersObject: this.enabledFilters.map(filter => ({ Id: filter.id, Name: filter.label }))
        }, { emitEvent: true });

        if (preset) {
          this.changeDetectionRef.detectChanges();
          this.formGroup.patchValue(preset, { emitEvent: true });
        } else {
          this.formGroup.reset(undefined, { emitEvent: true, onlySelf: true });
        }
      }))
      .pipe(switchMap(() => this.formGroup.valueChanges.pipe(auditTime(0))))
      .subscribe(async value => await this.filterService.setFiltersPreset(value));
  }

  async removeFilter(filter: FilterConfig) {
    this.enabledFilters = this.enabledFilters.filter(f => f.id !== filter.id);
    this.changeDetectionRef.detectChanges();
    await this.filterService.setFiltersPreset(this.formGroup.value);
  }

  async addFilter(filter: FilterConfig) {
    this.enabledFilters.push(filter);
    this.changeDetectionRef.detectChanges();
    await this.filterService.setFiltersPreset(this.formGroup.value);
  }

  async setActiveFilterPreset(id: number) {
    await firstValueFrom(this.filterService.setActiveFilterPreset(id));
  }

  public get isSavedFilterPreset(): boolean {
    return false;
  }

  async saveFilterPresetButtonClick() {
    var name = await firstValueFrom(this.dialog
      .open(FilterPresetNameFormDialogComponent, { panelClass: 'form-dialog' })
      .afterClosed());

    if (name) {
      await firstValueFrom(this.filterService.saveFilters(name));
    }
  }

  async unsaveFilterPresetButtonClick() {
    await firstValueFrom(this.filterService.unsaveFilters());
  }
}
